import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ContactTagsContainer } from "../ContactTagsContainer";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const DEFAULT_CONTACT_FIELD_REQUIREMENTS = {
	name: true,
	number: true,
	email: false,
};

const optionalMinMax = (min, max) =>
	Yup.string()
		.test("optional-min", () => i18n.t("validation.tooShort"), (value) => !value || value.length >= min)
		.max(max, () => i18n.t("validation.tooLong"));

const buildContactSchema = (fieldRequirements = DEFAULT_CONTACT_FIELD_REQUIREMENTS) =>
	Yup.object().shape({
		name: fieldRequirements.name
			? Yup.string().min(2, () => i18n.t("validation.tooShort")).max(50, () => i18n.t("validation.tooLong")).required(() => i18n.t("validation.required"))
			: optionalMinMax(2, 50),
		// Número é obrigatório só quando NÃO há email (contato de email pode não ter WhatsApp).
		number: Yup.string()
			.test("optional-min", () => i18n.t("validation.tooShort"), (value) => !value || value.length >= 8)
			.max(50, () => i18n.t("validation.tooLong"))
			.when("email", {
				is: (email) => !email || String(email).trim() === "",
				then: (schema) =>
					fieldRequirements.number ? schema.required(() => i18n.t("validation.required")) : schema,
				otherwise: (schema) => schema,
			}),
		email: fieldRequirements.email
			? Yup.string().email(() => i18n.t("validation.invalidEmail")).required(() => i18n.t("validation.required"))
			: Yup.string().email(() => i18n.t("validation.invalidEmail")),
	});

const ContactModal = ({
	open,
	onClose,
	contactId,
	initialValues,
	onSave,
	isGroup: isGroupProp = false,
	fieldRequirements = DEFAULT_CONTACT_FIELD_REQUIREMENTS
}) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
	};

	const [contact, setContact] = useState(initialState);
	
	// Detectar se é grupo baseado na prop ou no contato carregado
	// Verifica múltiplos formatos: boolean, número, string
	// NOTA: @lid é para contatos INDIVIDUAIS, não grupos! Grupos usam @g.us
	const isGroup = isGroupProp || 
		contact?.isGroup === true || 
		contact?.isGroup === 1 || 
		contact?.isGroup === "1" ||
		contact?.isGroup === "true" ||
		contact?.number?.includes("@g.us") || 
		/^\d+-\d+@g\.us$/.test(contact?.number) || // Formato: 554291453904-1477669484@g.us
		/^120363\d+@g\.us$/.test(contact?.number); // Formato: 120363406792162043@g.us

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					console.log(data)
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		try {
			if (contactId) {
				await api.put(`/contacts/${contactId}`, values);
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? isGroup 
							? i18n.t("contactModal.title.editGroup")
							: `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={buildContactSchema(fieldRequirements)}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									required={Boolean(fieldRequirements.name)}
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								{!isGroup && (
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										required={Boolean(fieldRequirements.number) && !values.email}
										error={touched.number && Boolean(errors.number)}
										helperText={
												(touched.number && errors.number) ||
												i18n.t("contactModal.form.numberHelper")
											}
										placeholder="5541998608485"
										variant="outlined"
										margin="dense"
									/>
								)}
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										required={Boolean(fieldRequirements.email)}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder={i18n.t("contactModal.form.emailPlaceholder")}
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
								{contactId && (
									<>
										<Typography
											style={{ marginBottom: 8, marginTop: 12 }}
											variant="subtitle1"
										>
											{i18n.t("contactModal.form.tags")}
										</Typography>
										<ContactTagsContainer contact={contact} />
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
